import { createApi } from '@reduxjs/toolkit/query/react';

import {
  AssessmentSubmission,
  CreateSupplierVettingRequestAPI,
  RequestMessage,
  RequestQuestion,
  SRAssessmentQuestion,
  Supplier,
  SupplierRequest,
  SupplierRequestInput,
  SupplierRequestMatch,
  SupplierVettingRequest,
  SupplierVettingRequestTemplate,
} from '@scaleup/models';
import { baseQuery } from './baseQuery';
import { TagTypes } from './tagTypes';

export const supplierRequestApi = createApi({
  reducerPath: 'supplierRequestApi',
  baseQuery: baseQuery,
  tagTypes: [TagTypes.supplierRequests, TagTypes.vettingRequests],
  endpoints: (builder) => ({
    getSupplierRequests: builder.query<SupplierRequest[], void>({
      query: () => '/supplier_request',
      providesTags: [TagTypes.supplierRequests],
    }),
    getImportedSupplierRequests: builder.query<SupplierRequest[], void>({
      query: () => '/supplier_request?imported=true',
      providesTags: [TagTypes.supplierRequestsImported],
    }),
    updateImportedToActive: builder.mutation<void, string>({
      query: (id) => ({
        url: `/supplier_request/${id}/imported`,
        method: 'PUT',
        body: {},
      }),
      invalidatesTags: [
        TagTypes.supplierRequests,
        TagTypes.supplierRequestsImported,
      ],
    }),
    archiveSupplierRequestMutation: builder.mutation<void, string>({
      query: (requestId) => ({
        url: `/supplier_request/${requestId}/archive`,
        method: 'PUT',
      }),
      invalidatesTags: [TagTypes.supplierRequests],
    }),
    createSupplierRequest: builder.mutation<void, SupplierRequestInput>({
      query: (supplierRequestInput) => ({
        url: '/supplier_request',
        method: 'POST',
        body: supplierRequestInput,
      }),
      invalidatesTags: [TagTypes.supplierRequests],
    }),
    saveSupplierRequestDraft: builder.mutation<void, SupplierRequestInput>({
      query: (supplierRequestInput) => ({
        url: '/supplier_request',
        method: 'POST',
        body: { ...supplierRequestInput, draft: true },
      }),
      invalidatesTags: [TagTypes.supplierRequests],
    }),
    generateSupplierRequest: builder.mutation<SupplierRequest, string>({
      query: (description) => ({
        url: '/supplier_request/ai',
        method: 'POST',
        body: {
          description,
        },
      }),
      invalidatesTags: [TagTypes.supplierRequests],
    }),
    getSrQuestions: builder.query<RequestQuestion[], string>({
      query: (id) => `/supplier_request/${id}/question`,
    }),
    answerSrQuestion: builder.mutation<
      void,
      {
        id: string;
        questionId: string;
        answer: string;
      }
    >({
      query: ({ id, questionId, answer }) => ({
        url: `/supplier_request/${id}/question/${questionId}`,
        method: 'POST',
        body: {
          answer,
        },
      }),
      invalidatesTags: [TagTypes.supplierRequests],
    }),
    getSrMatches: builder.query<SupplierRequestMatch[], string>({
      query: (id) => `/supplier_request/${id}/match`,
      providesTags: [TagTypes.supplierRequestMatches],
    }),
    getSrSupplier: builder.query<Supplier, { id: string; matchId: string }>({
      query: ({ id, matchId }) =>
        `/supplier_request/${id}/match/${matchId}/supplier`,
      providesTags: [TagTypes.supplierRequestMatchCandidate],
    }),
    updateSrTopPicks: builder.mutation<void, SupplierRequest>({
      query: (supplierRequest) => ({
        url: `/supplier_request/${supplierRequest.requestId}/top_picks`,
        method: 'PUT',
        body: supplierRequest,
      }),
      invalidatesTags: [TagTypes.supplierRequests],
    }),
    updateSrSuppliersUnderConsideration: builder.mutation<
      void,
      SupplierRequest
    >({
      query: (supplierRequest) => ({
        url: `/supplier_request/${supplierRequest.requestId}/suppliers_under_consideration`,
        method: 'PUT',
        body: supplierRequest,
      }),
      invalidatesTags: [TagTypes.supplierRequests],
    }),
    getSrMessages: builder.query<
      RequestMessage[],
      { id: string; matchId: string }
    >({
      query: ({ id, matchId }) =>
        `/supplier_request/${id}/match/${matchId}/message`,
      providesTags: [TagTypes.supplierRequestMatchMessage],
    }),
    createSrMessage: builder.mutation<
      void,
      { id: string; matchId: string; message: string }
    >({
      query: ({ id, matchId, message }) => ({
        url: `/supplier_request/${id}/match/${matchId}/message`,
        method: 'POST',
        body: {
          message,
        },
      }),
      invalidatesTags: [TagTypes.supplierRequestMatchMessage],
    }),
    acceptDeclineSrm: builder.mutation<
      void,
      { id: string; matchId: string; accept: boolean }
    >({
      query: ({ id, matchId, accept }) => ({
        url: `/supplier_request/${id}/match/${matchId}`,
        method: 'POST',
        body: {
          accept,
        },
      }),
      invalidatesTags: [TagTypes.supplierRequestMatches],
    }),
    srDealStatus: builder.mutation<void, { id: string; dealWon: boolean }>({
      query: ({ id, dealWon }) => ({
        url: `/supplier_request/${id}`,
        method: 'POST',
        body: {
          dealWon,
        },
      }),
      invalidatesTags: [TagTypes.supplierRequestMatches],
    }),
    uploadSupplierRequestDoc: builder.mutation<
      { key: string; presignedUrl: string },
      { fileName: string; contentType: string }
    >({
      query: (fileInfo) => ({
        url: '/supplier_request/upload',
        method: 'POST',
        body: fileInfo,
      }),
    }),
    deleteSupplierRequestDoc: builder.mutation<void, string>({
      query: (key) => ({
        url: `/supplier_request/upload?key=${key}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.supplierRequests],
    }),
    getSupplierRequestDoc: builder.mutation<{ presignedUrl: string }, string>({
      query: (key) => ({
        url: `/supplier_request/upload?key=${key}`,
        method: 'GET',
      }),
    }),
    //  vetting
    createVettingRequest: builder.mutation<
      void,
      { requestId: string; body: CreateSupplierVettingRequestAPI }
    >({
      query: ({ requestId, body }) => ({
        url: `/supplier_request/${requestId}/vetting_request`,
        method: 'POST',
        body: { ...body },
      }),
      invalidatesTags: (_res, _err, { requestId }) => [
        TagTypes.vettingRequests,
        { type: TagTypes.vettingTemplates, id: requestId },
      ],
    }),

    getVettingRequests: builder.query<SupplierVettingRequest[], string>({
      query: (requestId) => `/supplier_request/${requestId}/vetting_request`,
      providesTags: [TagTypes.vettingRequests],
    }),

    createVettingAssessmentQuestions: builder.mutation<
      SRAssessmentQuestion[],
      string
    >({
      query: (requestId) => ({
        url: `/supplier_request/${requestId}/vetting_request/assessment/questions`,
        method: 'POST',
        body: {},
      }),
    }),

    getVettingSubmission: builder.query<
      AssessmentSubmission,
      { requestId: string; vettingId: string }
    >({
      query: ({ requestId, vettingId }) =>
        `/supplier_request/${requestId}/vetting_request/${vettingId}/submission`,
    }),

    getVettingSRAScore: builder.query<
      { sraScore: 'running' | 'rising' | 'ready' },
      { requestId: string; vettingId: string }
    >({
      query: ({ requestId, vettingId }) =>
        `/supplier_request/${requestId}/vetting_request/${vettingId}/sra`,
    }),

    getVettingTemplates: builder.query<
      SupplierVettingRequestTemplate[],
      string
    >({
      query: (requestId) =>
        `/supplier_request/${requestId}/vetting_request/template`,
      providesTags: (_result, _error, id) => [
        { type: TagTypes.vettingTemplates, id },
      ],
    }),

    uploadVettingRequestDocument: builder.mutation<
      { key: string; presignedUrl: string },
      { requestId: string; fileName: string; contentType: string }
    >({
      query: ({ requestId, fileName, contentType }) => ({
        url: `/supplier_request/${requestId}/vetting_request/supporting_document`,
        method: 'POST',
        body: { contentType, fileName },
      }),
    }),

    getVettingRequestDocument: builder.query<
      { key: string; presignedUrl: string },
      { requestId: string; key: string }
    >({
      query: ({ requestId, key }) =>
        `/supplier_request/${requestId}/vetting_request/supporting_document/${key}`,
    }),

    deleteVettingRequestDocument: builder.mutation<
      void,
      { requestId: string; key: string }
    >({
      query: ({ requestId, key }) => ({
        url: `/supplier_request/${requestId}/vetting_request/supporting_document/${key}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetSupplierRequestsQuery,
  useCreateVettingAssessmentQuestionsMutation,
  useGenerateSupplierRequestMutation,
  useCreateSupplierRequestMutation,
  useSaveSupplierRequestDraftMutation,
  useGetSrQuestionsQuery,
  useAnswerSrQuestionMutation,
  useGetSrMatchesQuery,
  useGetSrSupplierQuery,
  useUpdateSrTopPicksMutation,
  useUpdateSrSuppliersUnderConsiderationMutation,
  useLazyGetSrMessagesQuery,
  useCreateSrMessageMutation,
  useAcceptDeclineSrmMutation,
  useSrDealStatusMutation,
  useUploadSupplierRequestDocMutation,
  useUploadVettingRequestDocumentMutation,
  useLazyGetVettingRequestDocumentQuery,
  useDeleteVettingRequestDocumentMutation,
  useDeleteSupplierRequestDocMutation,
  useGetSupplierRequestDocMutation,
  useUpdateImportedToActiveMutation,
  useGetImportedSupplierRequestsQuery,
  useCreateVettingRequestMutation,
  useGetVettingRequestsQuery,
  useGetVettingTemplatesQuery,
  useGetVettingSRAScoreQuery,
  useGetVettingSubmissionQuery,
  useArchiveSupplierRequestMutationMutation,
} = supplierRequestApi;
